import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
const brands = ['Columvi', 'Phesgo', 'Hemlibra', 'Gazyva', 'Risdiplam', 'Ocrevus'];
const matrix: Record<string, (boolean | null)[]> = {
  Germany: [true, false, false, false, false, false],
  France: [true, false, false, false, true, false],
  Spain: [true, false, false, false, false, false],
  UK: [false, true, false, false, false, false],
  Netherlands: [false, true, false, false, false, false],
  Belgium: [true, false, false, false, false, false],
  'South Africa': [false, false, true, false, false, false],
  Colombia: [false, false, true, false, false, false],
  Peru: [false, false, true, false, false, false],
  Egypt: [false, false, false, false, true, false],
  'Saudi Arabia': [false, false, false, false, true, false],
  Italy: [false, false, false, false, false, true],
  Slovakia: [false, false, false, false, false, true],
  Portugal: [false, false, false, true, false, false]
};
const scenarios = [{
  label: 'Baseline',
  desc: '6 brands · 14 countries (setup)',
  breakdown: '6×€2,500 (masters) + 14×€1,500 (countries)',
  value: '€36,000',
  color: 'bg-emerald-50 border-emerald-200',
  valueColor: 'text-emerald-600',
  tag: 'Current',
  tagColor: 'bg-emerald-100 text-emerald-700',
  note: 'one-off setup'
}, {
  label: 'Stage II',
  desc: '6 products in 14 countries potential alone',
  breakdown: '6 products × 14 countries',
  value: '€496,800',
  color: 'bg-indigo-50 border-indigo-200',
  valueColor: 'text-indigo-600',
  tag: 'Expansion',
  tagColor: 'bg-indigo-100 text-indigo-700',
  note: '/ 12 months'
}];
export function RocheBlueprintSlide() {
  const countries = Object.keys(matrix);
  return <div className="h-full flex flex-col gap-3" data-id="element-377">
      {/* Intro */}
      <motion.p initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="text-sm text-gray-500 leading-relaxed" data-id="element-378">
        How a few strategic pilots unlock{' '}
        <span className="text-purple-600 font-semibold" data-id="element-379">
          massive expansion opportunities
        </span>{' '}
        across multiple brands and countries.
      </motion.p>

      {/* Main two-column layout */}
      <div className="flex-1 grid grid-cols-5 gap-5 min-h-0" data-id="element-380">
        {/* LEFT: Brand-Country Matrix */}
        <motion.div initial={{
        opacity: 0,
        x: -12
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} className="col-span-3 flex flex-col rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm" data-id="element-381">
          {/* Matrix header */}
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50" data-id="element-382">
            <h3 className="text-sm font-bold text-purple-600" data-id="element-383">
              Roche: Brand-Country Expansion Matrix
            </h3>
            <div className="flex gap-4 mt-1" data-id="element-384">
              {[{
              val: '6',
              label: 'Total Brands',
              color: 'text-blue-600'
            }, {
              val: '14',
              label: 'Total Countries',
              color: 'text-purple-600'
            }, {
              val: '69',
              label: 'Expansion Opportunities',
              color: 'text-emerald-600'
            }].map((s, i) => <div key={i} className="flex items-baseline gap-1" data-id="element-385">
                  <span className={`text-base font-bold ${s.color}`} data-id="element-386">
                    {s.val}
                  </span>
                  <span className="text-[10px] text-gray-400" data-id="element-387">{s.label}</span>
                </div>)}
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto" data-id="element-388">
            <table className="w-full text-[10px]" data-id="element-389">
              <thead data-id="element-390">
                <tr className="border-b border-gray-100 bg-gray-50 sticky top-0" data-id="element-391">
                  <th className="text-left px-3 py-1.5 text-gray-400 font-semibold w-24" data-id="element-392">
                    Country
                  </th>
                  {brands.map(b => <th key={b} className="px-2 py-1.5 text-gray-500 font-semibold text-center" data-id="element-393">
                      {b}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-394">
                {countries.map((country, i) => <motion.tr key={country} initial={{
                opacity: 0,
                x: -8
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.3 + i * 0.04
              }} className="border-b border-gray-50 hover:bg-gray-50/50" data-id="element-395">
                    <td className="px-3 py-1.5 text-gray-600 font-medium" data-id="element-396">
                      {country}
                    </td>
                    {matrix[country].map((active, j) => <td key={j} className="px-2 py-1.5 text-center" data-id="element-397">
                        {active ? <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100" data-id="element-398">
                            <CheckCircle2 className="w-3 h-3 text-emerald-500" data-id="element-399" />
                          </span> : <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-50" data-id="element-400">
                            <XCircle className="w-3 h-3 text-red-300" data-id="element-401" />
                          </span>}
                      </td>)}
                  </motion.tr>)}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* RIGHT: Scaling Mathematics */}
        <motion.div initial={{
        opacity: 0,
        x: 12
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.25
      }} className="col-span-2 flex flex-col gap-3" data-id="element-402">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-3" data-id="element-403">
            <div data-id="element-404">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2" data-id="element-405">
                The Scaling Mathematics
              </p>
              <div className="flex flex-col gap-1 mb-1" data-id="element-406">
                <div className="flex items-baseline gap-1.5" data-id="element-407">
                  <span className="text-xl font-bold text-purple-600" data-id="element-408">
                    €2,500
                  </span>
                  <span className="text-xs text-gray-400" data-id="element-409">
                    per Master Snak{' '}
                    <span className="text-gray-300" data-id="element-410">(one-off per product)</span>
                  </span>
                </div>
                <div className="flex items-baseline gap-1.5" data-id="element-411">
                  <span className="text-lg font-bold text-indigo-600" data-id="element-412">
                    + €1,500
                  </span>
                  <span className="text-xs text-gray-400" data-id="element-413">
                    per country{' '}
                    <span className="text-gray-300" data-id="element-414">/ 6 months</span>
                  </span>
                </div>
                <div className="flex items-baseline gap-1.5" data-id="element-415">
                  <span className="text-lg font-bold text-violet-600" data-id="element-416">
                    + €350
                  </span>
                  <span className="text-xs text-gray-400" data-id="element-417">
                    per brand per country{' '}
                    <span className="text-gray-300" data-id="element-418">/ month</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2" data-id="element-419">
              {[{
              val: '110',
              label: 'Countries',
              color: 'text-blue-600'
            }, {
              val: '60',
              label: 'Brands',
              color: 'text-purple-600'
            }, {
              val: '6,600',
              label: 'Max Combos',
              color: 'text-amber-600'
            }].map((s, i) => <div key={i} className="rounded-xl bg-gray-50 border border-gray-100 px-2 py-2 text-center" data-id="element-420">
                  <div className={`text-lg font-bold ${s.color}`} data-id="element-421">{s.val}</div>
                  <div className="text-[10px] text-gray-400" data-id="element-422">{s.label}</div>
                </div>)}
            </div>

            {/* Revenue scenarios */}
            <div className="flex flex-col gap-2" data-id="element-423">
              {scenarios.map((s, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + i * 0.1
            }} className={`flex items-center justify-between px-3 py-2.5 rounded-xl border ${s.color}`} data-id="element-424">
                  <div data-id="element-425">
                    <div className="flex items-center gap-1.5 mb-0.5" data-id="element-426">
                      <span className="text-xs font-bold text-gray-700" data-id="element-427">
                        {s.label}
                      </span>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${s.tagColor}`} data-id="element-428">
                        {s.tag}
                      </span>
                    </div>
                    <span className="block text-[10px] text-gray-500 font-medium" data-id="element-429">
                      {s.desc}
                    </span>
                    <span className="block text-[9px] text-gray-300 font-mono mt-0.5" data-id="element-430">
                      {s.breakdown}
                    </span>
                  </div>
                  <div className="text-right shrink-0 ml-2" data-id="element-431">
                    <span className={`text-sm font-bold ${s.valueColor}`} data-id="element-432">
                      {s.value}
                    </span>
                    <span className="block text-[9px] text-gray-400" data-id="element-433">
                      {s.note}
                    </span>
                  </div>
                </motion.div>)}
            </div>

            <p className="text-[10px] text-gray-400 text-center leading-snug border-t border-gray-100 pt-2" data-id="element-434">
              Per client, per quarter — Roche alone represents massive recurring
              revenue potential
            </p>
          </div>

          {/* Bottom banner */}
          <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.85
        }} className="rounded-2xl bg-purple-600 px-4 py-3 shadow-md shadow-purple-200 text-center" data-id="element-435">
            <p className="text-sm font-bold text-white leading-snug" data-id="element-436">
              This is Just 1 of 35 Fortune 500 Pharma Organizations
            </p>
            <p className="text-xs text-purple-200 mt-0.5" data-id="element-437">
              Each with dozens of brands across hundreds of markets
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>;
}